import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import { getCodeOfConductEntriesApiV1ReportGeneratorCodeOfConductGet } from "../../api/generated/sdk.gen";
import { CodeOfConductEntrySchema } from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { CocEntry } from "../../constants/codeOfConduct";

/**
 * Code of Conduct reference content (MDRS-139).
 *
 * The declarations are served by GET /report-generator/code-of-conduct, seeded
 * server-side from the Confluence "Code of Conduct V3 - 13 July" page. This replaces the
 * old hardcoded `codeOfConduct.ts` constant so the legal wording can be updated
 * without a frontend deploy.
 */

const toCocEntry = (w: CodeOfConductEntrySchema): CocEntry => ({
  // The panel keys entries by their stable slug (matches the old constant ids).
  id: w.slug,
  category: w.category,
  label: w.label,
  serviceKind: w.service_kind ?? "",
  text: w.text,
});

export const useCodeOfConduct = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.CODE_OF_CONDUCT],
    enabled,
    // Reference content changes rarely; keep it warm for the session.
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<CocEntry[]> => {
      const resp = await getCodeOfConductEntriesApiV1ReportGeneratorCodeOfConductGet(
        { client: apiClient }
      );

      if (resp.error) {
        throw new Error(
          `Failed to load Code of Conduct entries: ${JSON.stringify(resp.error)}`
        );
      }
      return (resp.data ?? []).map(toCocEntry);
    },
  });
